/**
 * Cosmos R&D Symposium — published at /rd-symposium.
 *
 * Mirrors the Canva mockup. Six stacked sections under the shared NewNavBar +
 * NewFooter, alternating tan / white backgrounds:
 *   1. Hero        — date pill, italicised "Future of Knowledge" headline, stats
 *   2. The Premise — three-paragraph editorial intro
 *   3. What Was Discussed — Day 1 + Day 2 session lists
 *   4. From the Room — six case-study tiles
 *   5. Who Was in the Room — dot-separated institutions wall
 *   6. White Paper — copy + sign-up form (currently a dummy submit so we can
 *      ship the page before wiring up the API. Replace the setTimeout in
 *      `submitWhitepaper` with an apiService call when ready.)
 *
 * Mobile-first responsive: every grid collapses to one column below sm, the
 * stats row stays 3-col but with smaller text, and the institutions list
 * wraps naturally with dot separators. Reuses the same modal stack as About
 * Us so logging in from this page works without leaving.
 */

import React, { useEffect, useState } from 'react';
import { NewNavBar, NavItem, NewFooter } from './NewHome';
import { PrivacyPolicyModal } from './PrivacyPolicyModal';
import { TermsOfServiceModal } from './TermsOfServiceModal';
import { WhitePaperModal } from './WhitePaperModal';
import LoginModal from './LoginModal';
import SignUpModal from './SignUpModal';
import { SignupRequiredModal } from '../../components/SignupRequiredModal';
import { InlineAlert } from '../../components/InlineAlert';
import { trackClick } from '../../utils/analytics';
import { apiService } from '../../services/apiService';
import { friendlyAuthError } from '../../utils/authErrors';

// ---------------------------------------------------------------------------
// Static content
// ---------------------------------------------------------------------------

interface Session {
  num: number;
  title: string;
  format: string;
  speakers: string;
}

const SESSIONS_DAY_1: Session[] = [
  {
    num: 1,
    title: 'The Broken Compact: Why the Industry-Academia Model Needs Reinvention',
    format: 'Fireside chat',
    speakers: 'Ranjit Rajan, Dr. Sarah Almoaqiel, Dr. Dinesh Nambiar',
  },
  {
    num: 2,
    title: 'Strategic R&D Alliances: Partnership or Dependency?',
    format: 'Panel discussion',
    speakers: 'Prof. B.K.R. Naik, Jai Mrug, Komal Shah Bhukhanwala, Prof. Gurpreet Singh Jagpal OBE, Prof. Sachin Patwardhan, Rahul Singh, Dr. Ravindra Vikram Singh, Nilay Goyal',
  },
  {
    num: 3,
    title: 'The R&D Policy Gap: Are Governments Failing Innovation?',
    format: 'Panel discussion',
    speakers: 'Gaurav Vaid, Dr. Abdur Rub, Prasad Menon, Dr. Dinesh Nambiar, Dr. Malick Ndiaye, Biten Kathrani, Larry Hartman',
  },
  {
    num: 4,
    title: 'The Valley of Death Is Not a Metaphor',
    format: 'Expert talk',
    speakers: 'Joy Goswami',
  },
  {
    num: 5,
    title: 'From Lab to Market: Which Model Actually Works?',
    format: 'Panel discussion',
    speakers: 'Prof. Andrew Maxwell, Komal Shah Bhukhanwala, Praveen Malik, Satya Pandey, Tarek Sadek, Prof. Satheesh Krishnamurthy',
  },
];

const SESSIONS_DAY_2: Session[] = [
  {
    num: 6,
    title: 'Research Lightning Talks',
    format: 'Research talks',
    speakers: 'Prof. Prashant Mali, Dr. Ravi Raj, Venkat Narayanan, Dr. Srinivas Padmanabhuni',
  },
  {
    num: 7,
    title: 'CXO Fireside: Corporate Innovation — Reality Check',
    format: 'Fireside chat',
    speakers: 'Makarand Kaprekar, Satish Kumar, Avnish Sabharwal, Ranjit Rajan',
  },
  {
    num: 8,
    title: 'Open Innovation in Practice: Who Captures the Value?',
    format: 'Panel discussion',
    speakers: 'Gaurav Vaid, Biten Kathrani, Dr. Priya Hingorani, Dr. Dana Chase, Prof. B.K.R. Naik, Prof. Satheesh Krishnamurthy',
  },
];

interface CaseTile {
  category: string;
  title: string;
}

const CASES: CaseTile[] = [
  { category: 'TECH TRANSFER',       title: 'The touchscreen that became the iPhone — University of Delaware' },
  { category: 'OPEN INNOVATION',     title: 'India’s Unified Payments Interface (UPI) infrastructure' },
  { category: 'CORPORATE INNOVATION', title: 'Accenture Ventures — $1.5B in incremental sales from open innovation' },
  { category: 'HEALTHCARE',          title: 'CRISPR gene therapy for chronic pain — Novega Therapeutics (UC San Diego)' },
  { category: 'AI & DEEP TECH',      title: '30 billion molecules screened. One DMD drug candidate. Team of 8. — Peptris' },
  { category: 'POLICY',              title: 'Bayh-Dole Act — $1 trillion in GDP, 6.5M jobs, 200+ drugs in 45 years' },
];

const INSTITUTIONS = [
  'J&J', 'Merck', 'Accenture', 'Infosys', 'Godrej', 'Sandoz', 'Philips', 'Amazon',
  'SML', 'Miltenyi Biotec', 'Cisco', 'NASA',
  'IIT Bombay', 'UCLA', 'UC San Diego', 'Johns Hopkins University', 'Harvard Medical School',
  'MIT', 'University of Toronto', 'University of Surrey', 'Aston University',
  'State University of New York (SUNY)', 'Simon Fraser University', 'Tata Memorial Hospital',
  'Prince Sultan University', 'King Saud University', 'American University of Sharjah',
  'IIM Mumbai', 'Toronto Metropolitan University', 'York University',
  'India Translational Research Initiative (ITRI)', 'Innovance', 'Lab2Market',
  'NinjaScan.ai', 'Simsy.ai', 'Peptris', 'Tata Institute of Genomic Sciences (TIGS)',
  'AUTM', 'IDC',
];

const WHITEPAPER_BULLETS = [
  'The five unresolved tensions',
  'Session by session summaries and key exchanges',
  'Recommendations by stakeholder: universities, industry, government, TTOs, researchers',
  'Real proof points with key lessons',
  'Speaker index and full bios (30+ voices)',
];

// ---------------------------------------------------------------------------
// Small atoms
// ---------------------------------------------------------------------------

/** Gold-underline uppercase section heading, identical to AboutUsNew. */
const SectionTitle: React.FC<{ children: React.ReactNode; eventName: string }> = ({ children, eventName }) => {
  useEffect(() => { trackClick(`rd_symposium_section_view_${eventName}`); }, [eventName]);
  return (
    <div className="mb-6 sm:mb-8">
      <h2 className="text-xs sm:text-sm font-bold tracking-[0.25em] text-primary-700 uppercase">
        {children}
      </h2>
      <div className="mt-2 h-px w-24 sm:w-32 bg-secondary-og-600" />
    </div>
  );
};

const SessionList: React.FC<{ day: string; sessions: Session[] }> = ({ day, sessions }) => (
  <div>
    <div className="flex items-center gap-3 mb-3 sm:mb-4">
      <span className="text-[10px] sm:text-xs font-bold tracking-[0.25em] text-primary-700 uppercase">{day}</span>
      <div className="flex-1 h-px bg-grayscale-300" />
    </div>
    <div className="flex flex-col gap-2 sm:gap-3">
      {sessions.map(s => (
        <article
          key={s.num}
          className="rounded-lg bg-white border border-grayscale-200 hover:border-primary-300 transition-colors shadow-sm flex items-start gap-3 sm:gap-5 p-4 sm:p-5"
        >
          <span className="text-2xl sm:text-3xl font-bold text-primary-600 leading-none w-6 sm:w-9 shrink-0">
            {s.num}
          </span>
          <div className="min-w-0 flex-1">
            <h4 className="text-sm sm:text-base font-bold text-grayscale-1300 leading-snug">
              {s.title}
            </h4>
            <p className="mt-1 text-[11px] sm:text-xs text-grayscale-1100 leading-snug">
              <span className="text-primary-700 font-medium">{s.format}</span>
              <span className="text-grayscale-600">,</span>{' '}
              {s.speakers}
            </p>
          </div>
        </article>
      ))}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

interface RDSymposiumProps {
  onLogin: (email: string) => void;
}

const RDSymposium: React.FC<RDSymposiumProps> = ({ onLogin }) => {
  // Cross-page navigation uses a hard reload via `window.location.href`
  // instead of react-router-dom's `useNavigate`. This mirrors AboutUsNew —
  // useNavigate threw a Router-context error in the production bundle for
  // this page even though the component sits inside <BrowserRouter>, likely
  // a code-splitting / context-instance edge case. Hard nav also keeps the
  // auth handoff simple: after login, `onLogin` does the same window.location
  // pattern so MainWithClerk picks up freshly stored tokens.

  // ---- Auth modals (same stack as homepage / About Us) --------------------
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [showSignupRequiredModal, setShowSignupRequiredModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showWhitePaperModal, setShowWhitePaperModal] = useState(false);

  const closeAllAuthModals = () => {
    setShowLoginModal(false);
    setShowSignUpModal(false);
    setShowSignupRequiredModal(false);
  };

  const openLogin = () => { trackClick('rd_symposium_open_login'); closeAllAuthModals(); setShowLoginModal(true); };
  const openSignUp = () => { trackClick('rd_symposium_open_signup'); closeAllAuthModals(); setShowSignUpModal(true); };
  const handleSignUpSuccess = () => { setShowSignUpModal(false); setShowLoginModal(true); };

  const handleLoginClick = () => { trackClick('rd_symposium_nav_login_signup'); openLogin(); };
  const handleAboutUs = () => { trackClick('rd_symposium_nav_about_us'); window.location.href = '/about'; };
  const handleLogoClick = () => { trackClick('rd_symposium_nav_logo'); window.location.href = '/'; };

  const navItems: NavItem[] = [
    { id: 'about_us', label: 'About Us', href: '#', onClick: handleAboutUs },
  ];

  // ---- Page-view tracking -------------------------------------------------
  useEffect(() => { trackClick('rd_symposium_page_view'); }, []);

  // ---- Whitepaper form -----------------------------------------------------
  //
  // Wired to POST /api/v1/homepage/whitepaper which (a) records the request
  // in `whitepaper_requests` and (b) emails the PDF via SendGrid as an
  // attachment. Validation runs locally first so we fail fast on obviously
  // bad input without round-tripping; backend repeats the validation.
  const [whitepaperForm, setWhitepaperForm] = useState({ name: '', email: '', org: '' });
  const [whitepaperState, setWhitepaperState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [whitepaperError, setWhitepaperError] = useState('');

  const submitWhitepaper = async (e: React.FormEvent) => {
    e.preventDefault();
    trackClick('rd_symposium_whitepaper_submit', { email: whitepaperForm.email });

    // Validation-error funnel signal so analytics can tell "clicked submit
    // but bad input" apart from "clicked submit, API rejected the call".
    const missingName  = !whitepaperForm.name.trim();
    const missingEmail = !whitepaperForm.email.trim();
    const invalidEmail = !missingEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(whitepaperForm.email);
    if (missingName || missingEmail || invalidEmail) {
      setWhitepaperState('error');
      setWhitepaperError('Please enter your name and a valid email.');
      trackClick('rd_symposium_whitepaper_validation_error', {
        missing_name: missingName,
        missing_email: missingEmail,
        invalid_email: invalidEmail,
      });
      return;
    }
    setWhitepaperError('');
    setWhitepaperState('submitting');

    try {
      const res = await apiService.submitWhitepaperRequest({
        name: whitepaperForm.name.trim(),
        email: whitepaperForm.email.trim().toLowerCase(),
        organisation: whitepaperForm.org.trim() || undefined,
        form_source: 'rd_symposium_whitepaper',
      });
      if (res.success) {
        setWhitepaperState('success');
        trackClick('rd_symposium_whitepaper_submit_success', { email: whitepaperForm.email });
      } else {
        setWhitepaperState('error');
        setWhitepaperError(friendlyAuthError(res.error, 'Could not send the whitepaper email. Please try again.'));
        trackClick('rd_symposium_whitepaper_submit_error', { error: res.error });
      }
    } catch (err: any) {
      setWhitepaperState('error');
      setWhitepaperError(err?.message || 'Network error. Please check your connection and try again.');
      trackClick('rd_symposium_whitepaper_submit_error', { error: err?.message });
    }
  };

  return (
    <div className="bg-white min-h-screen font-dm-sans text-grayscale-1200 overflow-x-hidden">
      <NewNavBar
        navItems={navItems}
        onLoginClick={handleLoginClick}
        onLogoClick={handleLogoClick}
      />

      <main>
        {/* ============================= HERO ============================
            Horizontal padding (`px-4 sm:px-6 lg:px-8`) matches the public
            homepage's NavBar / Footer chrome so the editorial content
            column lines up with the brand-bar above and the footer below.
            Same applies to every other <section> on the page. */}
        <section className="bg-primary-100/40 px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 pb-10 sm:pb-16">
          <div className="max-w-screen-xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-secondary-100 border border-secondary-400 text-secondary-og-600 text-xs sm:text-sm font-bold tracking-wide">
              R&amp;D SYMPOSIUM · APRIL 2026
            </div>
            <p className="mt-4 sm:mt-5 text-xs sm:text-sm text-grayscale-1100 px-3">
              Held as part of PitchXPO · In association with MarketMate &amp; Pitchmatter
            </p>
            <h1 className="mt-5 sm:mt-7 text-3xl sm:text-5xl lg:text-6xl font-bold text-grayscale-1300 leading-tight px-3 max-w-4xl mx-auto">
              Who Controls the{' '}
              <span className="italic text-secondary-og-600">Future of Knowledge</span>
              ?
            </h1>
            <p className="mt-5 sm:mt-7 max-w-2xl mx-auto text-sm sm:text-base text-grayscale-1100 px-3 leading-relaxed">
              Two days. Thirty voices. Six countries. The conversations that need to happen — finally happening out loud.
            </p>

            {/* Stats — divide-x is only applied at sm+ to avoid a too-skinny
                divider when the labels stack on phone screens. */}
            <div className="mt-8 sm:mt-12 grid grid-cols-3 max-w-2xl mx-auto px-3 sm:divide-x sm:divide-grayscale-300">
              <div className="flex flex-col items-center px-2 sm:px-4">
                <span className="text-3xl sm:text-5xl font-bold text-grayscale-1300 leading-none">30+</span>
                <span className="mt-2 sm:mt-3 text-xs sm:text-sm text-grayscale-1100">Speakers</span>
              </div>
              <div className="flex flex-col items-center px-2 sm:px-4 border-x border-grayscale-300 sm:border-0">
                <span className="text-3xl sm:text-5xl font-bold text-grayscale-1300 leading-none">8</span>
                <span className="mt-2 sm:mt-3 text-xs sm:text-sm text-grayscale-1100">Sessions</span>
              </div>
              <div className="flex flex-col items-center px-2 sm:px-4">
                <span className="text-3xl sm:text-5xl font-bold text-grayscale-1300 leading-none">6</span>
                <span className="mt-2 sm:mt-3 text-xs sm:text-sm text-grayscale-1100">Countries</span>
              </div>
            </div>
          </div>
        </section>

        {/* ========================== THE PREMISE ======================== */}
        <section className="bg-white px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
          <div className="max-w-screen-xl mx-auto">
            <SectionTitle eventName="premise">The Premise</SectionTitle>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-grayscale-1300 leading-snug mb-5 sm:mb-6 max-w-4xl">
              We refused to curate agreement. We curated honesty.
            </h3>
            <div className="space-y-4 text-sm sm:text-base text-grayscale-1200 leading-relaxed max-w-3xl">
              <p>
                The conversations happening in{' '}
                <strong className="text-grayscale-1300">
                  university corridors, corporate boardrooms, government ministries, and startup garages
                </strong>{' '}
                — we put them all in the same room and refused to let them stay comfortable.
              </p>
              <p>
                A professor from IIT Bombay and a director from SUNY. A gynecologic oncologist from UCLA and a sustainability scientist from India. A former Accenture innovation veteran and a gene therapy pioneer from UC San Diego. A policy thinker from West Africa and an AI safety researcher from Bangalore.
              </p>
              <p>
                What emerged were not polished case studies — but{' '}
                <strong className="text-grayscale-1300">
                  five unresolved tensions at the heart of the global innovation system
                </strong>
                , and hard-won recommendations for everyone trying to change it.
              </p>
            </div>
          </div>
        </section>

        {/* ======================= WHAT WAS DISCUSSED ==================== */}
        <section className="bg-primary-100/40 px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
          <div className="max-w-screen-xl mx-auto">
            <SectionTitle eventName="what_was_discussed">What Was Discussed</SectionTitle>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-grayscale-1300 leading-snug mb-2 max-w-3xl">
              Eight sessions. No comfortable answers.
            </h3>
            <p className="text-sm sm:text-base text-grayscale-1100 mb-6 sm:mb-10 max-w-3xl">
              Each session was built around a provocative question, not a pre-agreed conclusion.
            </p>

            <SessionList day="Day 1" sessions={SESSIONS_DAY_1} />
            <div className="mt-8 sm:mt-10" />
            <SessionList day="Day 2" sessions={SESSIONS_DAY_2} />
          </div>
        </section>

        {/* ========================= FROM THE ROOM ======================= */}
        <section className="bg-white px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
          <div className="max-w-screen-xl mx-auto">
            <SectionTitle eventName="from_the_room">From the Room</SectionTitle>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-grayscale-1300 leading-snug mb-2 max-w-3xl">
              Real cases. Real lessons.
            </h3>
            <p className="text-sm sm:text-base text-grayscale-1100 mb-6 sm:mb-8 max-w-3xl">
              Every proof point was drawn from lived experience, not theoretical frameworks.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {CASES.map(c => (
                <article
                  key={c.title}
                  className="rounded-xl bg-primary-100/60 p-5 sm:p-6 hover:bg-primary-100 transition-colors"
                >
                  <p className="text-[10px] sm:text-xs font-bold tracking-[0.2em] text-primary-700 uppercase mb-2 sm:mb-3">
                    {c.category}
                  </p>
                  <p className="text-sm sm:text-base font-bold text-grayscale-1300 leading-snug">
                    {c.title}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ======================= WHO WAS IN THE ROOM ===================
            Matches the Canva mockup:
              - Eyebrow, headline and subtitle are left-aligned (same column
                as the rest of the page's editorial sections).
              - The dot-separated institutions list is rendered in the gold
                primary tone and visually centered as a "wall of names". */}
        <section className="bg-primary-100/40 px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
          <div className="max-w-screen-xl mx-auto">
            <SectionTitle eventName="who_was_in_the_room">Who Was in the Room</SectionTitle>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-grayscale-1300 leading-snug mb-3 sm:mb-4 max-w-3xl">
              Lived experience at leading, diverse institutions.
            </h3>
            <p className="text-sm sm:text-base text-grayscale-1100 mb-6 sm:mb-8 max-w-3xl">
              Researchers, commercial champions, corporate innovators, and policymakers from across the globe.
            </p>
            <p className="text-sm sm:text-base text-primary-700 leading-loose text-center max-w-5xl mx-auto">
              {INSTITUTIONS.map((inst, i) => (
                <React.Fragment key={inst}>
                  {inst}
                  {i < INSTITUTIONS.length - 1 && (
                    <span className="mx-1.5 sm:mx-2">·</span>
                  )}
                </React.Fragment>
              ))}
            </p>
          </div>
        </section>

        {/* ========================== WHITE PAPER ======================== */}
        <section className="bg-white px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
          <div className="max-w-screen-xl mx-auto">
            <SectionTitle eventName="white_paper">White Paper</SectionTitle>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
              {/* Left — copy + bullets */}
              <div>
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-grayscale-1300 leading-snug mb-3 sm:mb-4">
                  Everything from the room. Distilled.
                </h3>
                <p className="text-sm sm:text-base text-grayscale-1200 mb-4 sm:mb-5 leading-relaxed">
                  A curated synthesis of 30 voices, 8 sessions, and two days of productive friction — compiled for practitioners who want to go deeper.
                </p>
                <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-grayscale-1200">
                  {WHITEPAPER_BULLETS.map(b => <li key={b}>{b}</li>)}
                </ul>
              </div>

              {/* Right — dummy form */}
              <div className="rounded-2xl border border-primary-300 bg-white p-5 sm:p-7 w-full shadow-sm">
                {whitepaperState === 'success' ? (
                  <div className="text-center py-4 sm:py-6">
                    <div className="text-3xl mb-2">✓</div>
                    <h4 className="text-base sm:text-lg font-bold text-grayscale-1300 mb-2">
                      Check your inbox.
                    </h4>
                    <p className="text-sm text-grayscale-1100">
                      We’ve emailed the whitepaper to{' '}
                      <strong className="text-grayscale-1300 break-all">{whitepaperForm.email}</strong>. If you don’t see it in a minute or two, check your spam folder.
                    </p>
                  </div>
                ) : (
                  <>
                    <h4 className="text-base sm:text-lg font-bold text-grayscale-1300 mb-1">
                      Get the White Paper
                    </h4>
                    <p className="text-xs sm:text-sm text-grayscale-1100 mb-4">
                      We’ll send it to your inbox. No spam, ever.
                    </p>

                    <form onSubmit={submitWhitepaper} className="flex flex-col gap-3">
                      <div>
                        <label htmlFor="wp-name" className="block text-xs text-grayscale-1100 mb-1">Name</label>
                        <input
                          id="wp-name"
                          type="text"
                          value={whitepaperForm.name}
                          onChange={e => {
                            setWhitepaperForm(f => ({ ...f, name: e.target.value }));
                            if (whitepaperState === 'error') setWhitepaperState('idle');
                          }}
                          placeholder="Your full name"
                          className="w-full px-3 py-2.5 border border-grayscale-300 rounded-lg text-sm focus:outline-none focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label htmlFor="wp-email" className="block text-xs text-grayscale-1100 mb-1">Email</label>
                        <input
                          id="wp-email"
                          type="email"
                          value={whitepaperForm.email}
                          onChange={e => {
                            setWhitepaperForm(f => ({ ...f, email: e.target.value }));
                            if (whitepaperState === 'error') setWhitepaperState('idle');
                          }}
                          placeholder="you@company.com"
                          className="w-full px-3 py-2.5 border border-grayscale-300 rounded-lg text-sm focus:outline-none focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label htmlFor="wp-org" className="block text-xs text-grayscale-1100 mb-1">
                          Organisation (Optional)
                        </label>
                        <input
                          id="wp-org"
                          type="text"
                          value={whitepaperForm.org}
                          onChange={e => setWhitepaperForm(f => ({ ...f, org: e.target.value }))}
                          placeholder="Your organisation"
                          className="w-full px-3 py-2.5 border border-grayscale-300 rounded-lg text-sm focus:outline-none focus:border-primary-500"
                        />
                      </div>
                      <p className="text-[11px] text-grayscale-1100 leading-snug mt-1">
                        By submitting you agree to receive the white paper and occasional updates from UniversityTech.io. Unsubscribe any time.
                      </p>
                      {whitepaperState === 'error' && whitepaperError && (
                        <InlineAlert variant="error" compact>{whitepaperError}</InlineAlert>
                      )}
                      <div className="flex justify-end mt-1">
                        <button
                          type="submit"
                          disabled={whitepaperState === 'submitting'}
                          className="px-5 py-2.5 rounded-full bg-primary-600 hover:bg-primary-700 disabled:bg-grayscale-300 text-white text-sm font-medium inline-flex items-center justify-center gap-1.5 whitespace-nowrap"
                        >
                          <span>{whitepaperState === 'submitting' ? 'Sending…' : 'Send me the whitepaper'}</span>
                          <span aria-hidden="true">→</span>
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <NewFooter
        onShowAboutUs={() => { trackClick('rd_symposium_footer_about_us'); window.location.href = '/about'; }}
        onShowPrivacy={() => { trackClick('rd_symposium_footer_privacy'); setShowPrivacyModal(true); }}
        onShowTerms={() => { trackClick('rd_symposium_footer_terms'); setShowTermsModal(true); }}
        onShowRdSymposium={() => { trackClick('rd_symposium_footer_self'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        onShowWhitePaper={() => { trackClick('rd_symposium_footer_whitepaper'); setShowWhitePaperModal(true); }}
        onShowUniversities={() => { trackClick('rd_symposium_footer_universities'); window.location.href = '/universities'; }}
        onShowComet={() => { trackClick('rd_symposium_footer_comet'); window.location.href = '/comet'; }}
      />

      {showPrivacyModal && (
        <PrivacyPolicyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />
      )}
      {showTermsModal && (
        <TermsOfServiceModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
      )}
      <WhitePaperModal
        isOpen={showWhitePaperModal}
        onClose={() => setShowWhitePaperModal(false)}
        formSource="rd_symposium_footer_whitepaper_modal"
      />

      {/* Auth modals — same stack the homepage uses so log-in works in-place. */}
      {showLoginModal && (
        <LoginModal
          onClose={closeAllAuthModals}
          onLogin={onLogin}
          onSignUpClick={openSignUp}
        />
      )}
      {showSignUpModal && (
        <SignUpModal
          onClose={closeAllAuthModals}
          onSignupSuccess={handleSignUpSuccess}
        />
      )}
      {showSignupRequiredModal && (
        <SignupRequiredModal
          isOpen={showSignupRequiredModal}
          onClose={closeAllAuthModals}
          onLogin={openLogin}
          onSignup={openSignUp}
        />
      )}
    </div>
  );
};

export default RDSymposium;
